import React, { useEffect, useState, useCallback } from 'react';
import { useSvgStore } from '../../store/svgStore';
import { AiChatPage } from '../../../AIChatbotPlugin/pages/AiChatPage';
import SvgCanvasDesigner from '../canvas/SvgCanvasDesigner';
import PropertiesPanel from '../inspector/PropertiesPanel';
import SvgDetectedToast from '../ai/SvgDetectedToast';
import SvgFullscreenPreview from '../ai/SvgFullscreenPreview';
import { useSvgFromAI } from '../../hooks/useSvgFromAI';
import ToastContainer from '../common/Toast';
import '../../styles/ai-svg.css';

export interface AIHmiLayoutProps {
  businessType?: string;
  businessId?: string | number | null;
  /** 默认能力代码 */
  defaultCapabilityCode?: string;
}

const AIHmiLayout: React.FC<AIHmiLayoutProps> = ({
  businessType,
  businessId,
  defaultCapabilityCode,
}) => {
  // ─── Store 初始化 ─────────────────────
  const initNewCanvas = useSvgStore((s) => s.initNewCanvas);
  const loadAssets = useSvgStore((s) => s.loadAssets);
  const loadAiHistories = useSvgStore((s) => s.loadAiHistories);

  useEffect(() => {
    initNewCanvas();
    void loadAssets();
    // void loadAiHistories();
  }, [initNewCanvas, loadAssets, loadAiHistories]);

  // ─── SVG 解析 Hook（不再需要类型断言） ─────────────────────
  const {
    detectedSvg,
    handleAIMessage,
    applyToCanvas,
    saveToLibrary,
    exportSvgFile,
    dismiss,
    trackPrompt,
  } = useSvgFromAI();

  // ─── 全屏预览状态 ─────────────────────
  const [previewSvg, setPreviewSvg] = useState<string | null>(null);

  // ─── ★ 左侧面板展开/收起 ─────────────────────
  const [leftCollapsed, setLeftCollapsed] = useState(false);

  // ─── ★ 右侧面板展开/收起, 默认收起 ─────────────────────
  const [rightCollapsed, setRightCollapsed] = useState(true);

  // ─── ★ 设计按钮回调：直接将选中的 SVG 应用到画布 ─────────
  const handleDesign = useCallback((svgContent: string): void => {
    const { addAiSvgToCanvas, canvas } = useSvgStore.getState();
    const canvasWidth = canvas?.width ?? 1200;
    const canvasHeight = canvas?.height ?? 800;

    addAiSvgToCanvas(svgContent, 'AI 设计图层', {
      x: canvasWidth / 2,
      y: canvasHeight / 2,
    });
  }, []);

  const toggleLeftPanel = useCallback((): void => {
    setLeftCollapsed((prev) => !prev);
  }, []);

  const toggleRightPanel = useCallback((): void => {
    setRightCollapsed((prev) => !prev);
  }, []);

  const handlePreview = useCallback((): void => {
    if (detectedSvg) {
      setPreviewSvg(detectedSvg.svgContent);
    }
  }, [detectedSvg]);

  const closePreview = useCallback((): void => {
    setPreviewSvg(null);
  }, []);

  // ─── AiChatPage 回调（稳定引用，不会因渲染而变） ─────────

  const handleBeforeSend = useCallback((text: string): string => {
    return trackPrompt(text);
  }, [trackPrompt]);

  const handleMessageReceived = useCallback((message: string): void => {
    handleAIMessage(message);
  }, [handleAIMessage]);

  return (
    <div className="ai-svg-root-light">
      <div className={`ai-svg-shell-light ${leftCollapsed ? 'ai-svg-shell-left-collapsed' : ''} ${rightCollapsed ? 'ai-svg-shell-right-collapsed' : ''}`}>
        {/* ─── 左侧：AI 对话（复用 AiChatPage） ─── */}
        <div className={`ai-svg-panel ai-svg-panel-left-light ${leftCollapsed ? 'ai-svg-panel-left-collapsed' : ''}`}>
          <AiChatPage
            businessType={businessType}
            defaultCapabilityCode={defaultCapabilityCode}
            capabilityDisabled
            sidebarMode="drawer"
            onMessageReceived={handleMessageReceived}
            onBeforeSend={handleBeforeSend}
            showDesignButton
            onDesign={handleDesign}
          />
        </div>

        {/* ─── 中间：SVG 画布 ─── */}
        <SvgCanvasDesigner
          leftCollapsed={leftCollapsed}
          onToggleLeftPanel={toggleLeftPanel}
          rightCollapsed={rightCollapsed}
          onToggleRightPanel={toggleRightPanel}
        />

        {/* ─── 右侧：属性面板（含素材库 Tab） ─── */}
        <div className={`ai-svg-panel-right-slot ${rightCollapsed ? 'ai-svg-panel-right-slot--collapsed' : ''}`}>
          <PropertiesPanel />
        </div>
      </div>

      {/* ─── 浮层：SVG 检测操作条 ─── */}
      {/* {detectedSvg && (
        <SvgDetectedToast
          info={detectedSvg}
          onApplyToCanvas={applyToCanvas}
          onSaveToLibrary={saveToLibrary}
          onExport={exportSvgFile}
          onPreview={handlePreview}
          onDismiss={dismiss}
        />
      )} */}

      {/* ─── 浮层：全屏预览 ─── */}
      {previewSvg && (
        <SvgFullscreenPreview
          svgContent={previewSvg}
          onClose={closePreview}
        />
      )}

      {/* ─── 全局 Toast ─── */}
      <ToastContainer />
    </div>
  );
};

export default AIHmiLayout;